import { DAY_MS } from "@amber/contracts";
import type { RosterRow } from "@amber/application/contracts";
import { ActivityBars } from "@amber/collection/ui";
import { Portrait } from "@amber/profiles/ui";
import {
  AppLink,
  Status,
  cx,
  dataColumns,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataColumn,
  type VisibilityColumn,
} from "@amber/ui";
import { matches, toneOf } from "./rosterFilters";

/** Last new item older than this is emphasised, as on Sources (Sources brief: "highlighted when older than 3 days"). */
const QUIET_AFTER_MS = 3 * DAY_MS;

const stop = (event: { stopPropagation: () => void }) => {
  event.stopPropagation();
};

const monitored = (p: RosterRow) => p.lifecycle_state !== "incidental" && p.lifecycle_state !== "draft";
const dash = <span className="text-muted">—</span>;

const WORST_TONE: Record<string, string> = {
  failing: "text-attention font-strong",
  partial: "text-attention font-strong",
  late: "text-waiting font-strong",
  unusually_quiet: "text-fg font-strong",
};

function appearsIn(p: RosterRow) {
  if (p.appearances === 0) return <span className="text-muted">Not monitored</span>;
  return (
    <AppLink href={`/profiles/${p.id}?tab=items`} onClick={stop}>
      Appears in {p.appearances} item{p.appearances === 1 ? "" : "s"}
    </AppLink>
  );
}

function sourcesCell(p: RosterRow) {
  if (p.lifecycle_state === "incidental") return appearsIn(p);
  if (p.sources === 0) return p.plan ? "0" : <span className="text-muted">No plan yet</span>;
  const worst = p.worst_health;
  return (
    <span className="whitespace-nowrap">
      <AppLink href={p.sources_href} onClick={stop} className="text-link underline">
        {p.sources}
      </AppLink>
      {worst ? (
        <span className={cx("text-md", WORST_TONE[worst.state] ?? "text-muted")}>
          {" · "}
          {worst.state === "healthy" ? "healthy" : `${String(worst.count)} ${worst.label.toLowerCase()}`}
        </span>
      ) : null}
    </span>
  );
}

function activityCell(p: RosterRow) {
  if (!monitored(p)) return null;
  return (
    <span className="flex items-center gap-2">
      <ActivityBars daily={p.activity.daily} label={`Items found per day over 30 days for ${p.name}`} />
      <span className="font-mono text-md leading-tight">
        <b className="text-fg">{p.activity.items_7d}</b> 7 d<br />
        {p.activity.items_30d} 30 d
      </span>
    </span>
  );
}

function collectedCell(p: RosterRow) {
  if (!monitored(p)) return null;
  return (
    <span className="whitespace-nowrap font-mono text-md">
      <AppLink href={p.collected.href} onClick={stop} aria-label={`${String(p.collected.count)} items from the plan`}>
        {formatCount(p.collected.count)}
      </AppLink>
      {" · "}
      <AppLink
        href={`/profiles/${p.id}?tab=items`}
        onClick={stop}
        aria-label={`${String(p.appearances)} appearances as speaker elsewhere`}
      >
        {formatCount(p.appearances)}
      </AppLink>
      {" · "}
      <AppLink href={p.eligible.href} onClick={stop} aria-label={`${String(p.eligible.count)} Eligible communications`}>
        {formatCount(p.eligible.count)} Eligible
      </AppLink>
    </span>
  );
}

function countCell(p: RosterRow, link: RosterRow["open_gaps"], what: string) {
  if (!monitored(p) || link.count === 0) return null;
  return (
    <AppLink href={link.href} onClick={stop} aria-label={`${String(link.count)} ${what}`}>
      {link.count}
    </AppLink>
  );
}

function lastNewCell(p: RosterRow, now: number) {
  if (!monitored(p)) return null;
  if (!p.last_new_item) return dash;
  const quiet = now - new Date(p.last_new_item).getTime() > QUIET_AFTER_MS;
  return (
    <span className={cx(quiet && "font-strong text-fg")} title={formatDateTime(p.last_new_item)}>
      {formatRelative(p.last_new_item)}
    </span>
  );
}

const WIDTHS = {
  name: 220,
  status: 140,
  sources: 120,
  activity: 186,
  collected: 170,
  gaps: 64,
  downloads: 84,
  languages: 84,
  last: 100,
  owner: 140,
} as const;

export function rosterWidth(hidden: ReadonlySet<string>): number {
  return Object.entries(WIDTHS).reduce((sum, [id, width]) => sum + (hidden.has(id) ? 0 : width), 0);
}

const col = dataColumns<RosterRow>();

export function nameColumn(q: string, mutedIds: ReadonlySet<string>): DataColumn<RosterRow> {
  return col.display({
    id: "name",
    header: "Name",
    meta: { width: WIDTHS.name },
    cell: ({ row: { original: p } }) => {
      const dim = mutedIds.has(p.id);
      const finding = p.lifecycle_state === "draft" && (p.discovery === "queued" || p.discovery === "finding");
      return (
        <span className="flex items-center gap-2.5">
          <Portrait name={p.name} url={p.picture_url} muted={dim} />
          <span className="min-w-0">
            <b className={cx("block truncate font-strong", dim ? "text-muted" : "text-fg")}>{p.name}</b>
            <span className="block truncate text-md text-muted">
              {matches(p, q) || p.role || (finding ? "Finding sources…" : "")}
            </span>
          </span>
        </span>
      );
    },
  });
}

export function rosterColumns(now: number): readonly DataColumn<RosterRow>[] {
  return [
    col.display({
      id: "status",
      header: "Status",
      meta: { width: WIDTHS.status },
      cell: ({ row: { original: p } }) => (
        <span className="flex flex-col gap-0.5">
          <Status tone={toneOf(p.lifecycle_state)}>{p.state_label}</Status>
          {p.plan?.pending ? <span className="text-md font-strong text-waiting">Changes pending</span> : null}
        </span>
      ),
    }),
    col.display({
      id: "sources",
      header: "Sources",
      meta: { width: WIDTHS.sources },
      cell: ({ row }) => sourcesCell(row.original),
    }),
    col.display({
      id: "activity",
      header: "Items found · 30 days",
      meta: { width: WIDTHS.activity },
      cell: ({ row }) => activityCell(row.original),
    }),
    col.display({
      id: "collected",
      header: "Collected · all time",
      meta: { width: WIDTHS.collected },
      cell: ({ row }) => collectedCell(row.original),
    }),
    col.display({
      id: "gaps",
      header: "Open gaps",
      meta: { width: WIDTHS.gaps },
      cell: ({ row: { original: p } }) => countCell(p, p.open_gaps, "open gaps"),
    }),
    col.display({
      id: "downloads",
      header: "Download problems",
      meta: { width: WIDTHS.downloads },
      cell: ({ row: { original: p } }) => countCell(p, p.download_problems, "download problems"),
    }),
    col.display({
      id: "languages",
      header: "Languages",
      meta: { width: WIDTHS.languages },
      cell: ({ row }) => row.original.languages.map((l) => l.toUpperCase()).join(", "),
    }),
    col.display({
      id: "last",
      header: "Last new item",
      meta: { width: WIDTHS.last },
      cell: ({ row }) => lastNewCell(row.original, now),
    }),
    col.display({
      id: "owner",
      header: "Owner",
      meta: { width: WIDTHS.owner },
      cell: ({ row }) => row.original.owner?.name ?? dash,
    }),
  ];
}

export const ROSTER_VISIBILITY: readonly VisibilityColumn[] = [
  { id: "name", label: "Name", canHide: false },
  { id: "status", label: "Status" },
  { id: "sources", label: "Sources" },
  { id: "activity", label: "Items found · 30 days" },
  { id: "collected", label: "Collected · all time" },
  { id: "gaps", label: "Open gaps" },
  { id: "downloads", label: "Download problems" },
  { id: "languages", label: "Languages" },
  { id: "last", label: "Last new item" },
  { id: "owner", label: "Owner", hiddenByDefault: true },
];

/** Shown at 800 px: the pinned name, status and the columns that say whether monitoring works. */
export const NARROW_HIDDEN: readonly string[] = ["collected", "downloads", "languages", "last", "owner"];
